import { useMemo, useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reconApi } from '@/shared/api';
import type { ReconRow, ReconTxnPair } from '@/shared/api';
import { PdfViewer } from './PdfViewer';
import { MathModal } from './MathModal';
import { computeMathTrace, pfSignedQty } from './mathTrace';
import { pairUnpaired, type PairCandidate } from './pairUnpaired';

/**
 * Verify extraction page.
 *
 * Display-only side-by-side review:
 *   - Left:  hierarchical ISIN list grouped by DP file. Click an ISIN to
 *            select it; the PDF viewer jumps to its source page.
 *   - Right: PDF.js viewer showing the selected page.
 *   - Modal: opens on "Show math" click, displays the engine arithmetic
 *            (computed in mathTrace.ts).
 *
 * Data sources:
 *   - GET /api/recon/jobs/:id            → rows + txn pairs (already exists)
 *   - GET /api/recon/jobs/:id/dp_section_index  → per-DP file ISIN page index
 *   - GET /api/recon/jobs/:id/file/dp_N  → presigned URL to a DP PDF
 *
 * Failure modes:
 *   - Job not done: status banner, no PDF/math; reviewer comes back later.
 *   - No section index (job >24h, Redis evicted): fallback to page 1 +
 *     manual paging; ISIN list still renders.
 *   - DP file URL expired (>5min open): viewer shows a refresh hint.
 */

/**
 * Detect whether a DP file is a structured tabular format (CSV/Excel)
 * vs a PDF. Used by the right pane to decide between PdfViewer and
 * ParsedDpTable. Filename-based because that's what the worker
 * routes on too — keeps client and server consistent.
 *
 * Defensive: trims and lowercases. A file with no extension falls
 * through to the PDF path (PdfViewer will show its own error state
 * if the content isn't actually a PDF, which is the right failure
 * mode for an ambiguous file).
 */
function isStructuredDpFile(filename: string): boolean {
  const lower = (filename || '').trim().toLowerCase();
  return lower.endsWith('.csv') || lower.endsWith('.xlsx') || lower.endsWith('.xls');
}

export function VerifyExtractionPage() {
  const { id: jobId } = useParams<{ id: string }>();
  const nav = useNavigate();

  // Main job data — same fetch the JobDetailPage uses. React Query
  // dedupes between the two pages, so the response is hot from cache
  // when the operator clicks "Verify" from JobDetailPage.
  const jobQ = useQuery({
    queryKey: ['recon-job', jobId],
    queryFn: () => reconApi.get(jobId!),
    enabled: !!jobId,
  });

  // Section index — separate small endpoint that returns ISIN → page
  // map per DP file. Best-effort: empty list for old jobs is fine.
  const indexQ = useQuery({
    queryKey: ['recon-job-section-index', jobId],
    queryFn: () => reconApi.sectionIndex(jobId!),
    enabled: !!jobId,
    staleTime: 60_000,
  });

  // UI state
  const [selectedIsin, setSelectedIsin] = useState<string | null>(null);
  const [selectedDpIndex, setSelectedDpIndex] = useState<number>(0);
  const [pdfPage, setPdfPage] = useState<number>(1);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [mathRowId, setMathRowId] = useState<string | null>(null);
  /**
   * Left-pane sort order for the ISIN list.
   *   'page'   — group by DP file, ISIN order inside each group is the
   *              indexer's page order. Operators paging through a PDF
   *              top-to-bottom want this. Default.
   *   'status' — flat list grouped by status (QTY_MISMATCH first, then
   *              ONLY_IN_PORTFOLIO, ONLY_IN_DP, SLB_OUTSTANDING, MATCH).
   *              Operators triaging discrepancies want this — gets all
   *              the "needs attention" items into contiguous blocks.
   */
  const [isinSort, setIsinSort] = useState<'page' | 'status'>('page');
  /**
   * Index into the selected ISIN's `sourcesByIsin` list. Tracks which
   * source tab is active on the right pane. Reset to 0 (the first
   * source) on every ISIN change so the operator doesn't land on a
   * tab that doesn't exist for the new ISIN.
   */
  const [activeSourceIdx, setActiveSourceIdx] = useState<number>(0);
  /**
   * Source-pane layout. Sandeep's review feedback: scrolling between
   * PF and DP sources for the same ISIN is friction during a busy
   * mismatch triage. Side-by-side puts both visible at once, with the
   * portfolio rows on the left and the DP rows on the right. Falls back
   * to the single-source tabbed view on narrow screens (where two
   * dense tables side-by-side wouldn't fit) and when an ISIN has only
   * one source kind (no point splitting a single source in half).
   */
  const [sourceLayout, setSourceLayout] = useState<'tabs' | 'split'>('split');

  // Group rows by canonical_isin so the list maps to the section index.
  // The job-detail endpoint already returns rows sorted by status then
  // ISIN; we just bucket them.
  const rows: ReconRow[] = jobQ.data?.rows ?? [];
  const pairs: ReconTxnPair[] = jobQ.data?.txn_pairs ?? [];

  const pairsByRow = useMemo(() => {
    const m = new Map<string, ReconTxnPair[]>();
    for (const p of pairs) {
      const arr = m.get(p.row_id) ?? [];
      arr.push(p);
      m.set(p.row_id, arr);
    }
    return m;
  }, [pairs]);

  /**
   * Heuristic unpaired-row detection per ISIN. For each row in the
   * job, we attempt to pair its PF transactions against its DP
   * transactions by signed quantity + close date. Rows that fail to
   * pair are surfaced in the UI as "no close match — worth a look."
   *
   * This is NOT a causal "this row caused the gap" claim — net-balance
   * recon doesn't have unique causality. It's a triage aid that
   * narrows the operator's attention. See pairUnpaired.ts for the
   * full honest write-up.
   *
   * Computed for every ISIN (not just the selected one) so the
   * highlighting state is stable across selection changes.
   */
  const pairingByRow = useMemo(() => {
    const m = new Map<string, ReturnType<typeof pairUnpaired>>();
    for (const [rowId, ps] of pairsByRow) {
      const pfCands: PairCandidate[] = [];
      const dpCands: PairCandidate[] = [];
      for (const p of ps) {
        if (p.pf_txn_type != null) {
          // Skip OUTSIDE_WINDOW rows — they're not part of the recon
          // net, so they can't drive the in-window mismatch.
          if (p.status === 'OUTSIDE_WINDOW') continue;
          pfCands.push({
            pair_id: p.id,
            date: p.pf_txn_date ?? '',
            signed_qty: pfSignedQty(p.pf_txn_type, Number(p.pf_qty ?? 0)),
          });
        } else if (p.dp_description != null || p.dp_txn_date != null) {
          const credit = Number(p.dp_credit ?? 0);
          const debit = Number(p.dp_debit ?? 0);
          dpCands.push({
            pair_id: p.id,
            date: p.dp_txn_date ?? '',
            signed_qty: credit - debit,
          });
        }
      }
      m.set(rowId, pairUnpaired(pfCands, dpCands));
    }
    return m;
  }, [pairsByRow]);

  // Build the hierarchy: for each DP file, list its ISINs (from the
  // section index) with their recon_row. ISINs that have a recon_row
  // but no section-index entry still appear, just without a page hint.
  const dpFiles = indexQ.data?.files ?? [];
  const rowsByIsin = useMemo(() => {
    const m = new Map<string, ReconRow>();
    for (const r of rows) m.set(r.canonical_isin, r);
    return m;
  }, [rows]);

  // The hierarchy: for the section index, group sections by ISIN
  // (multi-sub-section ISINs have multiple entries).
  type DpFileGroup = {
    dp_index: number;
    filename: string;
    isins: Array<{
      isin: string;
      row: ReconRow | undefined;
      sections: Array<{ security_name: string; start_page: number; closing_balance: number | null }>;
    }>;
  };

  const fileGroups: DpFileGroup[] = useMemo(() => {
    // Two data sources for start_page, in priority order:
    //   1. section-index endpoint (full data — security_name per
    //      sub-section, closing_balance per section). Works for fresh
    //      jobs whose dp.index.payload events are still in Redis.
    //   2. row.dp_start_page (single number per ISIN, written at
    //      persist time by migration 012). Works for all v2-extracted
    //      jobs regardless of age. No sub-section detail, but we have
    //      the ISIN and the page.
    //
    // The endpoint also returns a list of DP files derived from
    // dp_s3_keys when sections are unavailable (Redis-evicted-events
    // fallback). For SINGLE-DP-FILE jobs the file list is sufficient
    // to render the verify page using only row.dp_start_page. For
    // multi-DP-file jobs without Redis events, we can't know which
    // file a given ISIN's page belongs to — we assume dp_index 0
    // (the first file) and the operator manually pages if wrong.
    return dpFiles.map(f => {
      const byIsin = new Map<string, DpFileGroup['isins'][number]>();

      // Source 1 — sections from indexer events
      for (const s of f.sections) {
        let entry = byIsin.get(s.isin);
        if (!entry) {
          entry = { isin: s.isin, row: rowsByIsin.get(s.isin), sections: [] };
          byIsin.set(s.isin, entry);
        }
        entry.sections.push({
          security_name: s.security_name,
          start_page: s.start_page,
          closing_balance: s.closing_balance,
        });
      }

      // Source 2 — for ISINs missing from the indexer events but
      // having row.dp_start_page, synthesize a pseudo-section.
      // Only do this for the FIRST DP file group (dp_index 0) so we
      // don't duplicate ISINs across files in multi-PDF jobs. For
      // single-PDF jobs (the common case) this is exactly right.
      if (f.dp_index === 0 && f.sections.length === 0) {
        for (const r of rows) {
          if (byIsin.has(r.canonical_isin)) continue;
          if (r.dp_start_page == null) continue;
          byIsin.set(r.canonical_isin, {
            isin: r.canonical_isin,
            row: r,
            sections: [{
              security_name: r.display_name ?? r.canonical_isin,
              start_page: r.dp_start_page,
              closing_balance: r.dp_closing_balance ?? null,
            }],
          });
        }
      }

      return { dp_index: f.dp_index, filename: f.filename, isins: Array.from(byIsin.values()) };
    });
  }, [dpFiles, rowsByIsin, rows]);

  // ISINs that exist in recon_row but not in any section index — show
  // these in an "Unindexed" bucket. Happens for ONLY_IN_PORTFOLIO (no
  // DP rows at all) or jobs whose dp.index.payload events were evicted.
  const indexedIsins = useMemo(() => {
    const s = new Set<string>();
    for (const f of dpFiles) for (const sec of f.sections) s.add(sec.isin);
    return s;
  }, [dpFiles]);
  const unindexedRows = rows.filter(r => !indexedIsins.has(r.canonical_isin));

  /**
   * Alternative grouping: by status label rather than by source DP file.
   *
   * Builds one group per status that's actually present in the job, in
   * triage priority order. Operators looking for "all the QTY_MISMATCH
   * rows" want them contiguous, not scattered across DP-file groups.
   *
   * Each group's ISINs carry the same metadata as the file-group view
   * (sections + row) so the right-pane source resolution still works.
   * For ISINs that exist in multiple DP files (uncommon but possible
   * with multi-DP jobs), we collapse to one entry — the first
   * encountered — since the status is per-ISIN, not per-DP-file.
   */
  /**
   * Effective status per row, computed from buildVfyBands — the same
   * matching engine the table uses. When bands produce zero red entries,
   * the ISIN is fully reconciled regardless of the stale backend status.
   * This fixes cases like Bajaj Finance where the backend stored
   * QTY_MISMATCH but the verify-page matching logic resolves to MATCH.
   */
  const effectiveStatusByRow = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of rows) {
      const rowPairs = pairsByRow.get(row.id) ?? [];
      if (rowPairs.length === 0) {
        m.set(row.id, row.status);
        continue;
      }
      try {
        const { bands } = buildVfyBands(rowPairs);
        const hasRed = bands.some(b => b.colour === 'red');
        if (!hasRed && row.status === 'QTY_MISMATCH') {
          m.set(row.id, 'MATCH');
        } else {
          m.set(row.id, row.status);
        }
      } catch {
        m.set(row.id, row.status);
      }
    }
    return m;
  }, [rows, pairsByRow]);

  type StatusGroup = {
    status: string;
    label: string;
    isins: Array<{
      isin: string;
      row: ReconRow;
      sections: Array<{ security_name: string; start_page: number; closing_balance: number | null }>;
    }>;
  };
  const statusGroups: StatusGroup[] = useMemo(() => {
    // Priority order — what triagers want to see first
    const STATUS_ORDER: Array<{ status: string; label: string }> = [
      { status: 'QTY_MISMATCH',         label: 'Quantity mismatch' },
      { status: 'ONLY_IN_PORTFOLIO',    label: 'Only in portfolio' },
      { status: 'ONLY_IN_DP',           label: 'Only in DP' },
      { status: 'SLB_OUTSTANDING',      label: 'SLB outstanding' },
      { status: 'ISIN_UNRESOLVABLE',    label: 'ISIN unresolvable' },
      { status: 'MATCH',                label: 'Match' },
    ];

    // Collect sections per ISIN across all DP files (first occurrence wins)
    const sectionsByIsin = new Map<string, StatusGroup['isins'][number]['sections']>();
    for (const f of dpFiles) {
      for (const s of f.sections) {
        if (!sectionsByIsin.has(s.isin)) sectionsByIsin.set(s.isin, []);
        sectionsByIsin.get(s.isin)!.push({
          security_name: s.security_name,
          start_page: s.start_page,
          closing_balance: s.closing_balance,
        });
      }
    }

    const out: StatusGroup[] = [];
    for (const { status, label } of STATUS_ORDER) {
      // Use effectiveStatusByRow (computed from buildVfyBands) so ISINs the
      // frontend resolves to MATCH don't appear stuck in their stale backend bucket.
      const matching = rows.filter(r => (effectiveStatusByRow.get(r.id) ?? r.status) === status);
      if (matching.length === 0) continue;
      out.push({
        status,
        label,
        isins: matching.map(r => ({
          isin: r.canonical_isin,
          row: r,
          sections: sectionsByIsin.get(r.canonical_isin) ?? (
            // Synthesize a pseudo-section if we have dp_start_page,
            // same fallback as file-group view.
            r.dp_start_page != null ? [{
              security_name: r.display_name ?? r.canonical_isin,
              start_page: r.dp_start_page,
              closing_balance: r.dp_closing_balance ?? null,
            }] : []
          ),
        })),
      });
    }

    // Any status not in the priority list (legacy values, etc.) goes
    // to a catch-all "Other" group so nothing silently disappears.
    const knownStatuses = new Set(STATUS_ORDER.map(s => s.status));
    const other = rows.filter(r => !knownStatuses.has(effectiveStatusByRow.get(r.id) ?? r.status));
    if (other.length > 0) {
      out.push({
        status: 'OTHER',
        label: 'Other',
        isins: other.map(r => ({
          isin: r.canonical_isin,
          row: r,
          sections: sectionsByIsin.get(r.canonical_isin) ?? [],
        })),
      });
    }
    return out;
  }, [rows, dpFiles]);

  /**
   * ISIN-centric source map. For each ISIN that has a row, lists EVERY
   * source where that ISIN appears:
   *   - one entry per DP file containing the ISIN (multi-DP-file jobs
   *     could have the same ISIN in both — e.g. customer with two
   *     demat accounts holding the same stock)
   *   - one entry for 'portfolio' if the ISIN has any pf_txn rows
   *
   * Drives the source-tab strip on the right pane. The previous version
   * only knew about ONE source per ISIN (the first DP file match), which
   * is the bug Sandeep hit: L&T appeared in two DP files but the verify
   * page only showed one.
   */
  type IsinSource =
    | {
        kind: 'dp';
        dp_index: number;
        filename: string;
        sub_sections: Array<{ label: string; start_page: number }>;
      }
    | {
        kind: 'portfolio';
        pf_pair_count: number;
      };

  const sourcesByIsin = useMemo(() => {
    const m = new Map<string, IsinSource[]>();

    // ─ DP sources: walk every file's sections, group by ISIN
    for (const f of dpFiles) {
      // Per-ISIN sub-sections within this file (Tata Elxsi has 3 sub-
      // sections all on different pages, for example)
      const subByIsin = new Map<string, Array<{ label: string; start_page: number }>>();
      for (const s of f.sections) {
        // Parse a trailing "(...)" off security_name as the sub-section
        // label — same convention the merger uses. Falls back to '(main)'
        // when there's no parenthetical (the common single-section case).
        const match = (s.security_name || '').match(/\(([^)]+)\)\s*$/);
        const label = match ? match[1].trim() : 'main';
        const arr = subByIsin.get(s.isin) ?? [];
        arr.push({ label, start_page: s.start_page });
        subByIsin.set(s.isin, arr);
      }
      for (const [isin, subs] of subByIsin.entries()) {
        // Sort sub-sections by their page so chips render in document
        // order — Beneficiary first, then Pledge, etc, regardless of
        // what order the indexer happened to emit them.
        subs.sort((a, b) => a.start_page - b.start_page);
        const list = m.get(isin) ?? [];
        list.push({ kind: 'dp', dp_index: f.dp_index, filename: f.filename, sub_sections: subs });
        m.set(isin, list);
      }
    }

    // ─ Fallback DP source: if a row has dp_start_page but the indexer
    //   never reported it (Redis evicted), synthesize a
    //   single dp_0 source so the operator still gets a tab.
    //   Only do this for ISINs NOT already covered above.
    for (const r of rows) {
      if (m.has(r.canonical_isin)) continue;
      if (r.dp_start_page == null) continue;
      const f0 = dpFiles[0];
      m.set(r.canonical_isin, [{
        kind: 'dp',
        dp_index: f0?.dp_index ?? 0,
        filename: f0?.filename ?? 'dp.pdf',
        sub_sections: [{ label: 'main', start_page: r.dp_start_page }],
      }]);
    }

    // ─ Structured-DP fallback: for CSV/Excel DP files, the indexer never
    //   runs (sections are empty) and there's no dp_start_page (no PDF
    //   pages exist). The two loops above miss these ISINs entirely.
    //
    //   Heuristic: any row with dp_txn_count > 0 has DP activity. We
    //   attribute it to dp_index 0 (the first DP file) as a pragmatic
    //   default. For single-DP-file jobs this is exactly right. For
    //   multi-DP-file jobs where the same ISIN exists in only ONE of
    //   the files, this could attribute it to the wrong file — fixing
    //   that properly needs a dp_file_index column on recon_row, which
    //   is on the deferred list.
    //
    //   This is the fix for the bug Sandeep would hit on ONLY_IN_DP
    //   rows from CSV jobs: every Vedanta-style row was showing 'No
    //   source documents contain this ISIN' even though the CSV
    //   clearly had a DP section for it.
    for (const r of rows) {
      if (m.has(r.canonical_isin)) continue;
      if ((r.dp_txn_count ?? 0) === 0) continue;
      const f0 = dpFiles[0];
      m.set(r.canonical_isin, [{
        kind: 'dp',
        dp_index: f0?.dp_index ?? 0,
        filename: f0?.filename ?? 'dp.csv',
        // Empty sub_sections array means the right-pane renderer will
        // hide the sub-section chip strip — there are no PDF pages
        // to navigate to. The ParsedDpTable will render the parsed
        // DP rows for this ISIN.
        sub_sections: [],
      }]);
    }

    // ─ Portfolio source: any row whose pairs include at least one
    //   pf_txn entry gets a portfolio tab. The pf_pair_count drives
    //   the tab badge.
    for (const r of rows) {
      const pfCount = (pairsByRow.get(r.id) ?? []).filter(p => p.pf_txn_date != null).length;
      if (pfCount === 0) continue;
      const list = m.get(r.canonical_isin) ?? [];
      list.push({ kind: 'portfolio', pf_pair_count: pfCount });
      m.set(r.canonical_isin, list);
    }

    return m;
  }, [dpFiles, rows, pairsByRow]);

  // Click an ISIN: pick its first source and load it. PDF-first if any
  // DP source exists; otherwise Portfolio tab (rare — would mean an
  // ONLY_IN_PORTFOLIO row was somehow selected through the indexed
  // pane, which the list rendering shouldn't allow).
  async function handleSelectIsin(isin: string) {
    setSelectedIsin(isin);
    setMathRowId(null);
    setActiveSourceIdx(0);
    const sources = sourcesByIsin.get(isin) ?? [];
    const first = sources[0];
    if (!first) return;
    if (first.kind === 'dp') {
      await loadDpSource(first.dp_index, first.sub_sections[0]?.start_page ?? 1, first.filename);
    }
    // Portfolio-first source needs no async work — the table renders
    // synchronously from pairsByRow.
  }

  /**
   * Fetch a fresh presigned URL for a DP file and switch the viewer
   * to it. Cached server-side for 5 minutes. We always fetch on a
   * different dp_index OR when there's no current URL — switching
   * back to a previously-loaded file gets a new URL because the
   * previous one might be near expiry by the time the operator
   * clicks back.
   *
   * For STRUCTURED DP files (.csv / .xlsx / .xls), there's no PDF to
   * load. We skip the URL fetch entirely — the right pane will render
   * a ParsedDpTable from recon_txn_pair data instead of PdfViewer.
   * The selectedDpIndex state is still updated so other UI elements
   * (e.g. file-name display) reflect the active source.
   */
  async function loadDpSource(dpIndex: number, startPage: number, filename: string) {
    if (isStructuredDpFile(filename)) {
      setSelectedDpIndex(dpIndex);
      setPdfPage(1);  // not meaningful for structured files; reset
      return;
    }
    if (dpIndex !== selectedDpIndex || pdfUrl === null) {
      try {
        const { url } = await reconApi.fileUrl(jobId!, `dp_${dpIndex}`);
        setPdfUrl(url);
        setSelectedDpIndex(dpIndex);
      } catch (err) {
        console.error('Failed to get DP file URL:', err);
      }
    }
    setPdfPage(startPage);
  }

  /**
   * Switch source tabs. The previous version assumed every source is
   * a PDF; now we have to branch on kind.
   */
  async function handleSwitchSource(sourceIdx: number) {
    if (!selectedIsin) return;
    const sources = sourcesByIsin.get(selectedIsin) ?? [];
    const src = sources[sourceIdx];
    if (!src) return;
    setActiveSourceIdx(sourceIdx);
    if (src.kind === 'dp') {
      await loadDpSource(src.dp_index, src.sub_sections[0]?.start_page ?? 1, src.filename);
    }
  }

  // Auto-select the first ISIN on first load so the page isn't empty.
  // useMemo dep on fileGroups handles the case where the section index
  // arrives a tick after jobQ resolves.
  const firstIsinSelected = useMemo(() => selectedIsin !== null, [selectedIsin]);
  useMemo(() => {
    if (firstIsinSelected) return;
    const firstFile = fileGroups[0];
    const firstIsin = firstFile?.isins[0];
    if (firstFile && firstIsin) {
      handleSelectIsin(firstIsin.isin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileGroups]);

  // Compute the math trace for the modal on demand.
  const mathTrace = useMemo(() => {
    if (!mathRowId) return null;
    const row = rows.find(r => r.id === mathRowId);
    if (!row) return null;
    const rowPairs = pairsByRow.get(row.id) ?? [];
    return computeMathTrace(row, rowPairs);
  }, [mathRowId, rows, pairsByRow]);

  /**
   * For the currently-selected ISIN, the unpaired pair-ids and the
   * unpaired-quantity totals on each side. Memoized so highlighting
   * doesn't recompute on unrelated rerenders.
   *
   * Only meaningful on QTY_MISMATCH; for MATCH / SLB_OUTSTANDING /
   * ONLY_IN_* the highlighting is suppressed at the call site since
   * those statuses already have a clear interpretation and unpaired-
   * row noise would be misleading.
   */
  const selectedIsinPairing = useMemo(() => {
    if (!selectedIsin) return undefined;
    const row = rows.find(r => r.canonical_isin === selectedIsin);
    if (!row) return undefined;
    return pairingByRow.get(row.id);
  }, [selectedIsin, rows, pairingByRow]);

  const selectedRow = useMemo(
    () => (selectedIsin ? rows.find(r => r.canonical_isin === selectedIsin) : undefined),
    [selectedIsin, rows],
  );

  const selectedEffectiveStatus = selectedRow
    ? (effectiveStatusByRow.get(selectedRow.id) ?? selectedRow.status)
    : undefined;

  // Auto-heal: same as TxnReconView — fire a silent background recompute for any
  // ISIN where buildVfyBands resolved QTY_MISMATCH → MATCH (stale DB values).
  const queryClient = useQueryClient();
  const recomputedRows = useRef(new Set<string>());
  useEffect(() => {
    if (!jobId) return;
    const staleRows = rows.filter(row => {
      if (recomputedRows.current.has(row.id)) return false;
      return (effectiveStatusByRow.get(row.id) === 'MATCH') &&
             row.status === 'QTY_MISMATCH' &&
             (pairsByRow.get(row.id) ?? []).length > 0;
    });
    for (const row of staleRows) {
      recomputedRows.current.add(row.id);
      reconApi.recomputeRow(jobId, row.id)
        .then(result => {
          if (result.changed) {
            queryClient.invalidateQueries({ queryKey: ['recon-job', jobId] });
          }
        })
        .catch(() => { /* silent */ });
    }
  }, [effectiveStatusByRow, rows, pairsByRow, jobId, queryClient]);

  // Math trace for the selected ISIN — used by the mismatch banner to show
  // reconciliation-set totals (pledge/eDIS-filtered), not raw backend values.
  const selectedRowTrace = useMemo(() => {
    if (!selectedRow) return null;
    const rowPairs = pairsByRow.get(selectedRow.id) ?? [];
    return computeMathTrace(selectedRow, rowPairs);
  }, [selectedRow, pairsByRow]);

  /** Highlighting only applies when the effective status is QTY_MISMATCH.
   *  Uses effectiveStatusByRow (from buildVfyBands) rather than the stale
   *  backend value — so ISINs the frontend resolves to MATCH (like Bajaj Finance)
   *  never show spurious unpaired banners or highlights. */
  const highlightUnpaired =
    selectedEffectiveStatus === 'QTY_MISMATCH' && selectedIsinPairing
      ? selectedIsinPairing.unpaired
      : undefined;

  if (!jobId) return <div className="p-6">Missing job id.</div>;
  if (jobQ.isLoading) return <div className="p-6 text-sm" style={{ color: 'var(--text-200)' }}>Loading job…</div>;
  if (jobQ.isError) return (
    <div className="p-6">
      <div className="text-sm" style={{ color: 'var(--status-warn)' }}>
        Could not load job: {(jobQ.error as Error).message}
      </div>
    </div>
  );

  const job = jobQ.data!;

  // What page-source coverage do we actually have?
  // - hasIndexerData:  the indexer's full per-section data is available
  //                    (best — gives sub-section labels + per-section
  //                    closing balances). Only present for fresh jobs
  //                    whose dp.index.payload events are still in Redis.
  // - hasRowData:      at least one row has dp_start_page (added by
  //                    migration 012; sufficient for single-PDF jobs
  //                    even after Redis history evicts).
  // - hasNoCoverage:   neither — page jumping won't work, reviewer
  //                    needs to navigate manually.
  const hasIndexerData = (indexQ.data?.files ?? []).some(f => f.sections.length > 0);
  const hasRowData     = rows.some(r => r.dp_start_page != null);
  const hasNoCoverage  = !hasIndexerData && !hasRowData;

  return (
    <div className="flex flex-col h-[calc(100vh-3rem)]">
      {/* Header bar */}
      <div className="card mx-4 mt-3 mb-2 px-4 py-2 flex items-center gap-3">
        <button
          type="button"
          onClick={() => nav(`/recon/${jobId}`)}
          className="text-xs px-2 py-1 rounded border border-axis-cloud-200"
        >‹ Back to job</button>
        <div className="flex-1">
          <div className="text-sm font-semibold" style={{ color: 'var(--text-strong)' }}>
            Verify extraction · <span className="font-mono text-xs">{jobId}</span>
          </div>
          <div className="text-[11px]" style={{ color: 'var(--text-200)' }}>
            Side-by-side review of extracted data and source PDF. Display only — no edits.
          </div>
        </div>
        <div className="text-[11px] flex gap-3" style={{ color: 'var(--text-200)' }}>
          <span>{dpFiles.length} DP file{dpFiles.length === 1 ? '' : 's'}</span>
          <span>{rows.length} ISIN{rows.length === 1 ? '' : 's'}</span>
          <span>{pairs.length} txn pair{pairs.length === 1 ? '' : 's'}</span>
        </div>
      </div>

      {hasNoCoverage && rows.length > 0 && (
        <div className="mx-4 mb-2 px-3 py-2 rounded text-[11px]"
             style={{
               background: 'color-mix(in srgb, var(--status-warn) 10%, transparent)',
               color: 'var(--status-warn)',
             }}>
          Source-page index is unavailable for this job (predates migration 012, and Redis history has aged out).
          The PDF viewer will default to page 1; use the page navigator to find the ISIN you're looking for.
        </div>
      )}

      {/* Split pane */}
      <div className="flex-1 flex gap-3 mx-4 mb-3 min-h-0">

        {/* Left pane — ISIN list */}
        <div className="card flex-1 max-w-md flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b border-axis-cloud-200 flex items-center justify-between"
               style={{ background: 'var(--bg-elevated)' }}>
            <span className="text-xs font-semibold" style={{ color: 'var(--text-strong)' }}>
              Extracted ISINs
            </span>
            {/* Sort toggle — page-order vs status-grouped. Default page-order
                preserves the existing behavior; status grouping is the
                triage-friendly view. */}
            <select
              value={isinSort}
              onChange={(e) => setIsinSort(e.target.value as 'page' | 'status')}
              className="text-[11px] rounded px-1.5 py-0.5"
              style={{
                background: 'var(--bg-surface)',
                color: 'var(--text-strong)',
                border: '1px solid var(--glass-border)',
              }}
              title="Sort the ISIN list by source-page order or by status label"
            >
              <option value="page">Sort: page order</option>
              <option value="status">Sort: status</option>
            </select>
          </div>
          <div className="flex-1 overflow-y-auto">
            {fileGroups.length === 0 && unindexedRows.length === 0 && (
              <div className="p-4 text-xs" style={{ color: 'var(--text-200)' }}>
                No ISINs to display. {job.status === 'processing' ? 'Job is still running.' : ''}
              </div>
            )}

            {isinSort === 'page' && fileGroups.map(fg => (
              <div key={fg.dp_index}>
                <div className="px-3 py-2 text-[11px] font-semibold sticky top-0 z-10"
                     style={{ background: 'var(--bg-elevated)', color: 'var(--text-200)' }}>
                  <span style={{ color: 'var(--text-strong)' }}>{fg.filename}</span>
                  <span className="ml-2">· {fg.isins.length} ISIN{fg.isins.length === 1 ? '' : 's'}</span>
                </div>
                {fg.isins.map(g => (
                  <IsinRow
                    key={g.isin}
                    isin={g.isin}
                    row={g.row}
                    sections={g.sections}
                    selected={selectedIsin === g.isin}
                    onSelect={() => handleSelectIsin(g.isin)}
                    onShowMath={() => g.row && setMathRowId(g.row.id)}
                    effectiveStatus={g.row ? (effectiveStatusByRow.get(g.row.id) ?? g.row.status) : undefined}
                  />
                ))}
              </div>
            ))}

            {isinSort === 'page' && unindexedRows.length > 0 && (
              <div>
                <div className="px-3 py-2 text-[11px] font-semibold sticky top-0 z-10"
                     style={{ background: 'var(--bg-elevated)', color: 'var(--text-200)' }}>
                  Unindexed ({unindexedRows.length})
                  <span className="ml-1 font-normal" style={{ color: 'var(--text-200)' }}>
                    — no source page available
                  </span>
                </div>
                {unindexedRows.map(r => (
                  <IsinRow
                    key={r.id}
                    isin={r.canonical_isin}
                    row={r}
                    sections={[]}
                    selected={selectedIsin === r.canonical_isin}
                    onSelect={() => handleSelectIsin(r.canonical_isin)}
                    onShowMath={() => setMathRowId(r.id)}
                    effectiveStatus={effectiveStatusByRow.get(r.id) ?? r.status}
                  />
                ))}
              </div>
            )}

            {isinSort === 'status' && statusGroups.map(sg => (
              <div key={sg.status}>
                <div className="px-3 py-2 text-[11px] font-semibold sticky top-0 z-10"
                     style={{ background: 'var(--bg-elevated)', color: 'var(--text-200)' }}>
                  <span style={{ color: 'var(--text-strong)' }}>{sg.label}</span>
                  <span className="ml-2">· {sg.isins.length} ISIN{sg.isins.length === 1 ? '' : 's'}</span>
                </div>
                {sg.isins.map(g => (
                  <IsinRow
                    key={g.isin}
                    isin={g.isin}
                    row={g.row}
                    sections={g.sections}
                    selected={selectedIsin === g.isin}
                    onSelect={() => handleSelectIsin(g.isin)}
                    onShowMath={() => g.row && setMathRowId(g.row.id)}
                    effectiveStatus={g.row ? (effectiveStatusByRow.get(g.row.id) ?? g.row.status) : undefined}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ─ Right pane — multi-source viewer ───────────────────────
            Tab strip across the top: one tab per source where the
            selected ISIN appears. PDF tabs render PDF.js; Portfolio
            tab renders the parsed pf_txn rows from recon_txn_pair as
            a table.

            For multi-sub-section ISINs (e.g. L&T Beneficiary + L&T
            Pledge) the PDF tab shows chips below the tab strip — one
            chip per sub-section with its page. Click a chip to jump
            the PDF to that page without leaving the tab.

            Empty state: when no ISIN is selected (initial load before
            jobs has resolved). After auto-select kicks in, this never
            shows. */}
        <div className="card flex-1 flex flex-col overflow-hidden">
          {(() => {
            const sources = selectedIsin ? (sourcesByIsin.get(selectedIsin) ?? []) : [];
            const activeSource = sources[activeSourceIdx];

            // Empty state: no ISIN selected yet
            if (!selectedIsin) {
              return (
                <div className="flex-1 flex items-center justify-center text-sm py-12"
                     style={{ color: 'var(--text-200)' }}>
                  Select an ISIN on the left to view its source(s).
                </div>
              );
            }

            // ISIN selected but no sources (shouldn't happen normally,
            // but a defensive empty state beats a broken page)
            if (sources.length === 0) {
              return (
                <div className="flex-1 flex items-center justify-center text-sm py-12 max-w-md mx-auto text-center"
                     style={{ color: 'var(--text-200)' }}>
                  No source documents contain this ISIN. The row exists
                  but neither the DP statements nor the portfolio Excel
                  has data for it — likely a synthetic or carry-over row.
                </div>
              );
            }

            return (
              <>
                {/* QTY_MISMATCH explainer banner. Sandeep's review feedback:
                    operators need to know WHY a client falls under quantity
                    mismatch — i.e. which specific transactions don't pair up
                    between portfolio and DP. The banner gives the net math
                    in plain English; the highlighted rows below show which
                    transactions had no close counterpart on the other side.
                    Suppressed on non-mismatch statuses where the highlight
                    would be misleading. */}
                {selectedEffectiveStatus === 'QTY_MISMATCH' && selectedIsinPairing && (
                  <div className="px-4 py-3 text-xs"
                       style={{
                         background: 'color-mix(in srgb, var(--status-warn) 12%, transparent)',
                         borderBottom: '2px solid var(--status-warn)',
                         color: 'var(--text-strong)',
                       }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold"
                            style={{
                              background: 'var(--status-warn)',
                              color: 'white',
                              letterSpacing: '0.06em',
                            }}>
                        Quantity Mismatch
                      </span>
                      <span className="font-mono text-[11px]" style={{ color: 'var(--text-200)' }}>
                        Portfolio {(selectedRowTrace?.pf.net ?? Number(selectedRow?.portfolio_qty ?? 0)).toLocaleString('en-IN')}
                        {' − '}DP {(selectedRowTrace?.dp.net ?? Number(selectedRow?.dp_qty ?? 0)).toLocaleString('en-IN')}
                        {' = '}
                        <span style={{ color: 'var(--status-warn)', fontWeight: 700 }}>
                          {((selectedRowTrace?.pf.net ?? Number(selectedRow?.portfolio_qty ?? 0)) - (selectedRowTrace?.dp.net ?? Number(selectedRow?.dp_qty ?? 0))).toLocaleString('en-IN')}
                        </span>
                      </span>
                    </div>
                    {selectedIsinPairing.unpaired.size > 0 ? (
                      <div style={{ color: 'var(--text-strong)' }}>
                        <strong>{selectedIsinPairing.unpaired.size} row{selectedIsinPairing.unpaired.size === 1 ? '' : 's'} below {selectedIsinPairing.unpaired.size === 1 ? 'is' : 'are'} highlighted</strong> —
                        {' '}these have no close-matching counterpart on the other side and are the best
                        starting point for investigation. (Unmatched PF qty: {selectedIsinPairing.unpaired_pf_qty.toLocaleString('en-IN')};
                        unmatched DP qty: {selectedIsinPairing.unpaired_dp_qty.toLocaleString('en-IN')}.)
                      </div>
                    ) : (
                      <div style={{ color: 'var(--text-200)' }}>
                        All transactions on both sides have a close match within ±7 days at the same
                        quantity. The mismatch may be caused by a misclassified transaction type, an
                        out-of-window adjustment, or quantity/sign rounding — open the math view for
                        a step-by-step breakdown.
                      </div>
                    )}
                  </div>
                )}

                {/* Tab strip */}
                <div className="flex items-center border-b border-axis-cloud-200 overflow-x-auto"
                     style={{ background: 'var(--bg-elevated)' }}>
                  {/* Side-by-side / Single toggle — only meaningful for
                      ISINs with BOTH a portfolio source and a DP source. */}
                  {(() => {
                    const pfPresent = sources.some(s => s.kind === 'portfolio');
                    const dpPresent = sources.some(s => s.kind === 'dp');
                    if (!(pfPresent && dpPresent)) return null;
                    return (
                      <div className="flex items-center gap-1 px-2 py-1.5 border-r whitespace-nowrap"
                           style={{ borderColor: 'var(--glass-border)' }}>
                        <button
                          type="button"
                          onClick={() => setSourceLayout('split')}
                          className="text-[11px] px-2 py-1 rounded transition"
                          style={{
                            background: sourceLayout === 'split'
                              ? 'color-mix(in srgb, var(--status-info) 18%, transparent)'
                              : 'transparent',
                            color: sourceLayout === 'split' ? 'var(--status-info)' : 'var(--text-200)',
                            fontWeight: sourceLayout === 'split' ? 600 : 400,
                          }}
                          title="Show portfolio and DP side-by-side"
                        >
                          Side-by-side
                        </button>
                        <button
                          type="button"
                          onClick={() => setSourceLayout('tabs')}
                          className="text-[11px] px-2 py-1 rounded transition"
                          style={{
                            background: sourceLayout === 'tabs'
                              ? 'color-mix(in srgb, var(--status-info) 18%, transparent)'
                              : 'transparent',
                            color: sourceLayout === 'tabs' ? 'var(--status-info)' : 'var(--text-200)',
                            fontWeight: sourceLayout === 'tabs' ? 600 : 400,
                          }}
                          title="Single source at a time, switched by tab"
                        >
                          Single
                        </button>
                      </div>
                    );
                  })()}
                  {sources.map((s, idx) => {
                    const active = idx === activeSourceIdx;
                    const isStructured = s.kind === 'dp' && isStructuredDpFile(s.filename);
                    const label = s.kind === 'dp' ? s.filename : 'Portfolio Excel';
                    // Badge content depends on what's meaningful for the
                    // source: PDF DPs have sub-sections (pages); structured
                    // DPs have txns (no pages); portfolio always has txns.
                    let badge: string;
                    if (s.kind === 'portfolio') {
                      badge = `${s.pf_pair_count} ${s.pf_pair_count === 1 ? 'txn' : 'txns'}`;
                    } else if (isStructured) {
                      // Sub-sections are empty for CSV/Excel; count DP txns
                      // for this ISIN from the rows array instead. A "txns"
                      // label parallels the portfolio tab and is more
                      // informative than "0 sections".
                      const row = rows.find(r => r.canonical_isin === selectedIsin);
                      const dpCount = row?.dp_txn_count ?? 0;
                      badge = `${dpCount} ${dpCount === 1 ? 'DP txn' : 'DP txns'}`;
                    } else {
                      badge = `${s.sub_sections.length} ${s.sub_sections.length === 1 ? 'section' : 'sections'}`;
                    }
                    // Icon reflects what's actually in the file: 📄 for
                    // PDF (rendered via PDF.js), 📊 for structured tabular
                    // (rendered as a parsed-row table).
                    const icon = isStructured || s.kind === 'portfolio' ? '📊' : '📄';
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSwitchSource(idx)}
                        className="flex items-center gap-2 px-4 py-2.5 text-xs whitespace-nowrap border-b-2 transition"
                        style={{
                          borderBottomColor: active ? 'var(--status-info)' : 'transparent',
                          color: active ? 'var(--text-strong)' : 'var(--text-200)',
                          fontWeight: active ? 600 : 400,
                          background: active ? 'var(--bg-base)' : 'transparent',
                        }}
                        title={s.kind === 'dp' ? `Open ${s.filename}` : 'View parsed portfolio rows'}
                      >
                        <span>{icon}</span>
                        <span>{label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded"
                              style={{
                                background: 'color-mix(in srgb, var(--text-200) 12%, transparent)',
                                color: 'var(--text-200)',
                              }}>
                          {badge}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Sub-section chips — only for active PDF DP tab (CSV/Excel
                    DPs don't have page numbers; the parsed table shows all
                    sub-sections inline), only when there's more than one
                    sub-section. Multi-sub-section ISINs (Beneficiary +
                    Pledge + Unconfirmed Pledge) get one chip per sub-
                    section so the operator can jump between them without
                    paging manually. */}
                {activeSource?.kind === 'dp'
                  && !isStructuredDpFile(activeSource.filename)
                  && activeSource.sub_sections.length > 1 && (
                  <div className="px-3 py-2 border-b border-axis-cloud-200 flex items-center gap-2 flex-wrap"
                       style={{ background: 'var(--bg-elevated)' }}>
                    <span className="text-[10px] uppercase tracking-wider"
                          style={{ color: 'var(--text-200)' }}>
                      Sub-sections:
                    </span>
                    {activeSource.sub_sections.map((sub, i) => {
                      const isCurrent = pdfPage === sub.start_page;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setPdfPage(sub.start_page)}
                          className="text-[11px] px-2 py-0.5 rounded transition"
                          style={{
                            background: isCurrent
                              ? 'color-mix(in srgb, var(--status-info) 18%, transparent)'
                              : 'color-mix(in srgb, var(--text-200) 8%, transparent)',
                            color: isCurrent ? 'var(--status-info)' : 'var(--text-strong)',
                            border: isCurrent
                              ? '1px solid color-mix(in srgb, var(--status-info) 40%, transparent)'
                              : '1px solid transparent',
                          }}
                          title={`Jump to ${sub.label} (page ${sub.start_page})`}
                        >
                          {sub.label} · p.{sub.start_page}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Layout toggle + active source viewer.
                    Sandeep's review feedback: scrolling between PF and DP for
                    the same ISIN is friction. Side-by-side shows both at
                    once when both exist; tabbed mode preserves the legacy
                    one-at-a-time behavior. The toggle is only shown for
                    ISINs that have both a portfolio and a DP source — the
                    split layout wouldn't add value otherwise. */}
                {(() => {
                  const pfSource = sources.find(s => s.kind === 'portfolio');
                  const dpSources = sources.filter(s => s.kind === 'dp');
                  const eligible = !!pfSource && dpSources.length > 0;
                  const useSplit = sourceLayout === 'split' && eligible;
                  // When in split mode, the DP column shows whichever DP
                  // source is currently "active" via the tab strip (the
                  // operator can flip DP files for multi-DP jobs without
                  // leaving split mode). If the active idx points at the
                  // PF tab, default to the first DP source.
                  const activeIsDp = activeSource?.kind === 'dp';
                  const activeDpForSplit = activeIsDp ? activeSource : dpSources[0];

                  if (!useSplit) {
                    return (
                      <>
                        {activeSource?.kind === 'dp' && !isStructuredDpFile(activeSource.filename) && (
                          <PdfViewer url={pdfUrl} page={pdfPage} onPageChange={setPdfPage} />
                        )}
                        {activeSource?.kind === 'dp' && isStructuredDpFile(activeSource.filename) && selectedIsin && (
                          <ParsedDpTable
                            isin={selectedIsin}
                            filename={activeSource.filename}
                            rows={rows}
                            pairsByRow={pairsByRow}
                            unpairedIds={highlightUnpaired}
                          />
                        )}
                        {activeSource?.kind === 'portfolio' && selectedIsin && (
                          <ParsedPfTable isin={selectedIsin} rows={rows} pairsByRow={pairsByRow} unpairedIds={highlightUnpaired} />
                        )}
                      </>
                    );
                  }

                  // Split layout: PF on the left, active DP source on the right.
                  // min-w-0 on the flex children is load-bearing: without it,
                  // each table's intrinsic content width (8 columns of padding
                  // + content) forces the flex parent to grow, and the second
                  // column wraps below the first — exactly the "stacked one
                  // after another" symptom Om reported. min-w-0 lets each
                  // child shrink below content width; the inner overflow-auto
                  // then handles horizontal scrolling within that column.
                  return (
                    <div className="flex-1 flex overflow-hidden">
                      <div className="flex-1 min-w-0 flex flex-col overflow-hidden"
                           style={{ borderRight: '1px solid var(--glass-border)' }}>
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold"
                             style={{
                               background: 'color-mix(in srgb, var(--text-200) 6%, transparent)',
                               color: 'var(--text-200)',
                               borderBottom: '1px solid var(--glass-border)',
                             }}>
                          Portfolio
                        </div>
                        <div className="flex-1 min-w-0 overflow-auto">
                          {selectedIsin && (
                            <ParsedPfTable isin={selectedIsin} rows={rows} pairsByRow={pairsByRow} unpairedIds={highlightUnpaired} />
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold flex items-center justify-between"
                             style={{
                               background: 'color-mix(in srgb, var(--text-200) 6%, transparent)',
                               color: 'var(--text-200)',
                               borderBottom: '1px solid var(--glass-border)',
                             }}>
                          <span>DP statement</span>
                          {dpSources.length > 1 && activeDpForSplit && (
                            <span className="font-normal normal-case tracking-normal text-[10px]"
                                  style={{ color: 'var(--text-200)' }}>
                              {activeDpForSplit.kind === 'dp' ? activeDpForSplit.filename : ''}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 overflow-auto">
                          {activeDpForSplit?.kind === 'dp' && !isStructuredDpFile(activeDpForSplit.filename) && (
                            <PdfViewer url={pdfUrl} page={pdfPage} onPageChange={setPdfPage} />
                          )}
                          {activeDpForSplit?.kind === 'dp' && isStructuredDpFile(activeDpForSplit.filename) && selectedIsin && (
                            <ParsedDpTable
                              isin={selectedIsin}
                              filename={activeDpForSplit.filename}
                              rows={rows}
                              pairsByRow={pairsByRow}
                              unpairedIds={highlightUnpaired}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            );
          })()}
        </div>
      </div>

      {/* Math modal */}
      {mathTrace && <MathModal trace={mathTrace} onClose={() => setMathRowId(null)} />}

      <Link to={`/recon/${jobId}`} className="sr-only">Back to job</Link>
    </div>
  );
}

function IsinRow({
  isin, row, sections, selected, onSelect, onShowMath, effectiveStatus,
}: {
  isin: string;
  row: ReconRow | undefined;
  sections: Array<{ security_name: string; start_page: number; closing_balance: number | null }>;
  selected: boolean;
  onSelect: () => void;
  onShowMath: () => void;
  /** Effective status computed from buildVfyBands — may differ from row.status
   *  when the frontend matching resolves a stale backend QTY_MISMATCH to MATCH. */
  effectiveStatus?: string;
}) {
  const firstPage = sections[0]?.start_page ?? 1;
  const name = row?.display_name ?? sections[0]?.security_name ?? '(unknown)';
  const displayStatus = effectiveStatus ?? row?.status;
  const statusColor =
    displayStatus === 'MATCH'             ? 'var(--status-ok)'
  : displayStatus === 'SLB_OUTSTANDING'   ? 'var(--status-info)'
  : displayStatus === 'QTY_MISMATCH'      ? 'var(--status-warn)'
  : displayStatus === 'ONLY_IN_PORTFOLIO' ? 'var(--text-200)'
  : displayStatus === 'ONLY_IN_DP'        ? 'var(--text-200)'
  : 'var(--text-200)';

  const subTxt =
    `${isin}`
    + (sections.length > 0 ? ` · page ${firstPage}` : '')
    + (sections.length > 1 ? ` · ${sections.length} sub-sections` : '')
    + (row ? ` · ${row.portfolio_txn_count ?? 0}pf / ${row.dp_txn_count ?? 0}dp txns` : '');

  return (
    <div
      onClick={onSelect}
      className="px-3 py-2 border-b border-axis-cloud-200 cursor-pointer"
      style={{
        background: selected
          ? 'color-mix(in srgb, var(--status-info) 8%, transparent)'
          : 'transparent',
        borderLeft: selected ? '3px solid var(--status-info)' : '3px solid transparent',
      }}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate" style={{ color: 'var(--text-strong)' }}>
            {name}
          </div>
          <div className="text-[10px] font-mono truncate" style={{ color: 'var(--text-200)' }}>
            {subTxt}
          </div>
        </div>
        {row && (
          <>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap"
              style={{
                background: `color-mix(in srgb, ${statusColor} 14%, transparent)`,
                color: statusColor,
              }}
            >
              {(displayStatus ?? row.status).replace(/_/g, ' ')}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onShowMath(); }}
              className="text-[10px] px-1.5 py-0.5 rounded border border-axis-cloud-200"
              style={{ color: 'var(--text-200)' }}
            >Show math</button>
          </>
        )}
      </div>
    </div>
  );
}

/**
/* ─── Shared matching helpers (same rules as TxnReconView) ──────────────
 *
 * BUY  → DP CREDIT within 1–3 days, same total qty
 * SELL / TRANSFEROUT / BUYBACK → DP DEBIT exact same date, same total qty
 * DIVIDEND / INTERIM DIVIDEND / FINAL DIVIDEND → auto-green, no match needed
 * Unmatched either side → red
 * ─────────────────────────────────────────────────────────────────────── */

const VFY_DIVIDEND_TYPES = new Set([
  'DIVIDEND', 'FINAL DIVIDEND', 'INTERIM DIVIDEND', 'FINALDIVIDEND', 'INTERIMDIVIDEND',
]);
/** SELL rows — grouped by date, total qty matched to DP DEBIT same day. */
// VFY_SELL_GROUP_TYPES removed — grouping handled via sellGroupMap
/** Individual-delivery rows — each matched separately; first in order wins. */
const VFY_SELL_INDIV_TYPES = new Set(['TRANSFEROUT', 'TRANSFER OUT', 'BUYBACK']);
const VFY_SELL_TYPES = new Set(['SELL', 'TRANSFEROUT', 'TRANSFER OUT', 'BUYBACK']);
const VFY_BUY_TYPES  = new Set(['BUY', 'BUYCR', 'BONUS', 'SPLIT BUY', 'DEMERGERIN', 'DEMERGERBUY',
  'Split Buy', 'Bonus Buy', 'Rights Buy', 'Merger Buy', 'ROC Buy',
  // Uppercase variants (old jobs where parser uppercased the label)
  'SPLIT BUY', 'BONUS BUY', 'RIGHTS BUY', 'MERGER BUY', 'ROC BUY']);

/** Extract settlement code from a DP description.
 *  "By CM AXIS SECURITIES LIMITED,T+1 NORMAL/2023142/..." → "2023142"
 *  Returns null when no NORMAL/ pattern is found. */
function isEdisDpLine(description: string | null | undefined): boolean {
  if (!description) return false;
  const d = description.toLowerCase();
  return d.includes('edis') || d.includes('block mechanism');
}
/** True when a DP description is a pledge/margin internal operation. */
function isPledgeDescriptionVfy(description: string | null | undefined): boolean {
  if (!description) return false;
  const d = description.toLowerCase();
  return d.includes('to pledge') || d.includes('by pledge') ||
         d.includes('pledge request') || d.includes('pledge closure') ||
         d.includes('pledge rejection') || d.includes('pledge cancellation') ||
         d.includes('margin re-pledge');
}
/** Deduplicate eDIS / Block Mechanism lines within a DP group (ISIN+code).
 *  Rule: eDIS lines are booked as exact duplicate pairs — every eDIS line
 *  has an identical twin (same qty, same direction). Keep exactly HALF
 *  the rows of each (qty+direction) group (divide count by 2).
 *  Examples:
 *    [35, 35]         → keep one 35         (pair of 2 → 1)
 *    [6, 6, 18, 18]   → keep one 6, one 18  (two pairs → 2)
 *  Non-eDIS lines (CM NSE Clearing, CM Axis, etc.) are never touched. */
function dedupeEdisLines(rows: ReconTxnPair[]): ReconTxnPair[] {
  // Separate eDIS and non-eDIS
  const nonEdis: ReconTxnPair[] = [];
  const edisByKey = new Map<string, ReconTxnPair[]>();

  for (const row of rows) {
    if (!isEdisDpLine(row.dp_description)) {
      nonEdis.push(row);
      continue;
    }
    const qty = Number(row.dp_credit ?? 0) || Number(row.dp_debit ?? 0);
    const dir = (row.dp_credit ?? 0) > 0 ? 'cr' : 'dr';
    const key = `${qty}|${dir}`;
    if (!edisByKey.has(key)) edisByKey.set(key, []);
    edisByKey.get(key)!.push(row);
  }

  // For each (qty+direction) group, keep exactly half (divide by 2)
  const dedupedEdis: ReconTxnPair[] = [];
  for (const [, group] of edisByKey.entries()) {
    const keepCount = Math.ceil(group.length / 2); // half, rounding up for odd edge-case
    dedupedEdis.push(...group.slice(0, keepCount));
  }

  return [...nonEdis, ...dedupedEdis];
}
/** Extract settlement code from DP description.
 *  Supports all known DP settlement prefixes:
 *    NORMAL/          — standard T+1 exchange settlement
 *    TRADE FOR TRADE/ — trade-for-trade segment (e.g. T group stocks)
 *  Extracts the run of digits immediately after the prefix, regardless of
 *  any other text, spacing, or non-ASCII characters elsewhere in the string.
 *  Examples:
 *    "By CM AXIS,T+1 NORMAL/2023112/xyz"         → "2023112"
 *    "By CM AXIS,T+1 ¿ NORMAL/2023112"           → "2023112"
 *    "By CM AXIS,T+1 ¿ TRADE FOR TRADE/2023126"  → "2023126"
 */
function extractDpSettlementCode(description: string | null | undefined): string | null {
  if (!description) return null;
  const m = description.match(/(?:NORMAL|TRADE FOR TRADE)\/([0-9]+)/i);
  return m ? m[1].trim() : null;
}


/** True when a DP credit has no genuine exchange settlement code — eligible for Tier-2. */
function isTier2CreditVfy(description: string | null | undefined): boolean {
  if (!description || description.trim() === '') return true;
  const d = description.toLowerCase();
  if (d.includes('edis') || d.includes('block mechanism')) return false;
  return !extractDpSettlementCode(description);
}

interface VfyBand {
  /** Empty array for a DP-only band (unmatched DP row, no PF counterpart). */
  pfRows: ReconTxnPair[];
  dpRow:  ReconTxnPair | null;
  colour: 'green' | 'red';
  isGroup: boolean;
  /** Chronological sort key — PF row date for PF bands, DP row date for
   *  DP-only bands. Every band sorts into ONE sequence so an old
   *  unmatched DP entry never gets stranded below newer matched rows. */
  sortDate: string;
}

function buildVfyBands(allPairs: ReconTxnPair[]): { bands: VfyBand[] } {
  const pfPairs = allPairs
    .filter(p => p.pf_txn_date != null && p.status !== 'OUTSIDE_WINDOW')
    .sort((a, b) => (a.pf_txn_date! > b.pf_txn_date! ? 1 : -1));

  const dpPairs = allPairs
    .filter(p => p.pf_txn_date == null && p.dp_txn_date != null)
    .sort((a, b) => (a.dp_txn_date! > b.dp_txn_date! ? 1 : -1));

  const usedDp = new Set<string>();

  // BUY rows: group same-date buys (combined qty must match one DP credit within 1-3 days).
  // SELL / TRANSFEROUT / BUYBACK: each row is its OWN band — NOT grouped.
  //   Two TRANSFEROUT 27 on the same date = two separate deliveries.
  //   Only the first one (chronologically) gets the matching DP debit of 27.
  //   Grouping them (54 total) would never match a single debit of 27.
  // DIV: auto-green, group by date.
  type PfGroup = { date: string; typeClass: string; rows: ReconTxnPair[] };
  const buyGroupMap  = new Map<string, PfGroup>();
  const sellGroupMap = new Map<string, PfGroup>();
  const divGroupMap  = new Map<string, PfGroup>();
  const caGroupMap   = new Map<string, PfGroup>(); // Corporate Action bonus rows
  const orderedBands: VfyBand[] = [];

  for (const p of pfPairs) {
    const raw     = (p.pf_txn_type ?? '').toUpperCase().trim(); // for SELL/DIV lookup
    const origRaw = (p.pf_txn_type ?? '').trim();                    // verbatim for CA check
    let tc = 'OTHER';
    if (VFY_DIVIDEND_TYPES.has(raw))                                           tc = 'DIV';
    else if (VFY_BUY_TYPES.has(raw) || VFY_BUY_TYPES.has(origRaw) || origRaw.endsWith('Buy')) tc = 'BUY';
    else if (VFY_SELL_TYPES.has(raw))                                          tc = 'SELL';

    if (tc === 'DIV') {
      const key = `${p.pf_txn_date}|DIV`;
      if (!divGroupMap.has(key)) divGroupMap.set(key, { date: p.pf_txn_date!, typeClass: 'DIV', rows: [] });
      divGroupMap.get(key)!.rows.push(p);
      continue;
    }
    if (tc === 'BUY') {
      // Only Bonus/Rights rows lack a NORMAL/ settlement code in the DP —
      // they must be matched by date+qty via caGroupMap.
      // Split/Merger/ROC rows DO have settlement codes → go via buyGroupMap.
      const caTypeLabel = origRaw;
      const isCARow = caTypeLabel === 'Bonus Buy' || caTypeLabel === 'Rights Buy' ||
        caTypeLabel === 'BONUS BUY' || caTypeLabel === 'RIGHTS BUY';
      if (isCARow) {
        const key = `${p.pf_txn_date}|CA`;
        if (!caGroupMap.has(key)) caGroupMap.set(key, { date: p.pf_txn_date!, typeClass: 'CA', rows: [] });
        caGroupMap.get(key)!.rows.push(p);
        continue;
      }
      const key = `${p.pf_txn_date}|BUY`;
      if (!buyGroupMap.has(key)) buyGroupMap.set(key, { date: p.pf_txn_date!, typeClass: 'BUY', rows: [] });
      buyGroupMap.get(key)!.rows.push(p);
      continue;
    }
    if (tc === 'SELL') {
      const rawT = (p.pf_txn_type ?? '').toUpperCase().trim();
      if (VFY_SELL_INDIV_TYPES.has(rawT)) {
        // TRANSFEROUT / BUYBACK — each row individually; first wins
        const qty = Math.abs(Number(p.pf_qty) ?? 0);
        const date = p.pf_txn_date!;
        let matched: ReconTxnPair | null = null;
        for (const dp of dpPairs) {
          if (usedDp.has(dp.id)) continue;
          const debit = Number(dp.dp_debit ?? 0);
          if (debit <= 0 || dp.dp_txn_date !== date) continue;
          if (Math.abs(debit - qty) < 0.001) { matched = dp; break; }
        }
        if (matched) usedDp.add(matched.id);
        orderedBands.push({ pfRows: [p], dpRow: matched, colour: matched ? 'green' : 'red', isGroup: false, sortDate: date });
      } else {
        // SELL — group by date; total must match one DP debit on same day
        const key = `${p.pf_txn_date}|SELL`;
        if (!sellGroupMap.has(key)) sellGroupMap.set(key, { date: p.pf_txn_date!, typeClass: 'SELL', rows: [] });
        sellGroupMap.get(key)!.rows.push(p);
      }
      continue;
    }
    // OTHER
    orderedBands.push({ pfRows: [p], dpRow: null, colour: 'red', isGroup: false, sortDate: p.pf_txn_date! });
  }

  // Flush DIV groups (auto-green)
  for (const { date: divDate, rows } of divGroupMap.values()) {
    orderedBands.push({ pfRows: rows, dpRow: null, colour: 'green', isGroup: rows.length > 1, sortDate: divDate });
  }

  // Flush CA groups — Corporate Action rows matched by ISIN + date + qty
  for (const { date, rows } of caGroupMap.values()) {
    const totalQty = rows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
    let matched: ReconTxnPair | null = null;
    for (const dp of dpPairs) {
      if (usedDp.has(dp.id)) continue;
      const credit = Number(dp.dp_credit ?? 0);
      if (credit <= 0) continue;
      if (extractDpSettlementCode(dp.dp_description)) continue; // skip normal trades
      if (dp.dp_txn_date !== date) continue;
      if (Math.abs(credit - totalQty) < 0.001) { matched = dp; break; }
    }
    if (matched) usedDp.add(matched.id);
    orderedBands.push({ pfRows: rows, dpRow: matched, colour: matched ? 'green' : 'red', isGroup: rows.length > 1, sortDate: date });
  }

  // Flush BUY groups — re-group by settlement code so each unique code gets
  // its own DP match. Sum ALL DP credits with the same code (eDIS can split
  // one settlement into multiple entries, e.g. 1496 + 104 = 1600).
  const buyByCode = new Map<string, { code: string | null; date: string; rows: ReconTxnPair[] }>();
  for (const { rows } of buyGroupMap.values()) {
    for (const row of rows) {
      const code = row.pf_particulars?.trim() || null;
      const mapKey = code ?? 'NOCODE';
      if (!buyByCode.has(mapKey)) buyByCode.set(mapKey, { code, date: row.pf_txn_date!, rows: [] });
      buyByCode.get(mapKey)!.rows.push(row);
    }
  }

  // Flush SELL groups — intraday-aware with date+qty fallback
  const sellByCode = new Map<string, { code: string | null; date: string; rows: ReconTxnPair[] }>();
  for (const { rows } of sellGroupMap.values()) {
    for (const row of rows) {
      const code = row.pf_particulars?.trim() || null;
      const mapKey = code ?? 'NOCODE_SELL';
      if (!sellByCode.has(mapKey)) sellByCode.set(mapKey, { code, date: row.pf_txn_date!, rows: [] });
      sellByCode.get(mapKey)!.rows.push(row);
    }
  }

  const dpDebitByCode = new Map<string, ReconTxnPair[]>();
  for (const dp of dpPairs) {
    const debit = Number(dp.dp_debit ?? 0);
    if (debit <= 0) continue;
    const dpCode = extractDpSettlementCode(dp.dp_description);
    if (!dpCode) continue;
    if (!dpDebitByCode.has(dpCode)) dpDebitByCode.set(dpCode, []);
    dpDebitByCode.get(dpCode)!.push(dp);
  }

  for (const { code: pfCode, date: sellDate, rows: allSellRows } of sellByCode.values()) {
    const sellRows = allSellRows.filter(r => !usedDp.has(r.id + '__pfUsed'));
    if (sellRows.length === 0) continue;
    // Remove intraday sells — BUY code may differ from SELL code, so check
    // all available BUY rows on the same date (not just same-code BUYs).
    const usedBuyIdx     = new Set<number>();
    const nonIntradaySells: ReconTxnPair[] = [];
    const intradaySellIds  = new Set<string>();

    const allBuyRowsFlat: ReconTxnPair[] = [];
    for (const { rows: brows } of buyByCode.values()) {
      for (const r of brows) {
        if (!usedDp.has(r.id + '__pfUsed')) allBuyRowsFlat.push(r);
      }
    }
    const buyRowsForCode = allBuyRowsFlat;

    const buysByDate = new Map<string, { indices: number[]; total: number }>();
    for (let i = 0; i < buyRowsForCode.length; i++) {
      const d = buyRowsForCode[i].pf_txn_date ?? '';
      if (!buysByDate.has(d)) buysByDate.set(d, { indices: [], total: 0 });
      const g = buysByDate.get(d)!;
      g.indices.push(i);
      g.total += Math.abs(Number(buyRowsForCode[i].pf_qty) ?? 0);
    }

    const sellsByDate = new Map<string, ReconTxnPair[]>();
    for (const sell of sellRows) {
      const d = sell.pf_txn_date ?? '';
      if (!sellsByDate.has(d)) sellsByDate.set(d, []);
      sellsByDate.get(d)!.push(sell);
    }
    const usedBuyDate = new Set<string>();

    for (const [date, sellsOnDate] of sellsByDate.entries()) {
      const sellTotal = sellsOnDate.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      const buyGroup  = buysByDate.get(date);

      if (buyGroup && !usedBuyDate.has(date)) {
        if (Math.abs(buyGroup.total - sellTotal) < 0.001) {
          // Fully intraday
          buyGroup.indices.forEach(i => usedBuyIdx.add(i));
          usedBuyDate.add(date);
          sellsOnDate.forEach(sell => intradaySellIds.add(sell.id));
        } else {
          // Partial intraday: find individual SELL whose qty == BUY group total
          for (const sell of sellsOnDate) {
            const sellQty = Math.abs(Number(sell.pf_qty) ?? 0);
            if (Math.abs(buyGroup.total - sellQty) < 0.001) {
              buyGroup.indices.forEach(i => usedBuyIdx.add(i));
              usedBuyDate.add(date);
              intradaySellIds.add(sell.id);
              break;
            }
          }
          sellsOnDate.forEach(sell => {
            if (!intradaySellIds.has(sell.id)) nonIntradaySells.push(sell);
          });
        }
      } else {
        sellsOnDate.forEach(sell => nonIntradaySells.push(sell));
      }
    }

    if (nonIntradaySells.length === 0) continue;

    const totalQty = nonIntradaySells.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
    let matchedDps: ReconTxnPair[] = [];

    if (pfCode) {
      const sellIsin   = nonIntradaySells[0]?.canonical_isin ?? '';
      const dpSellKey  = `${sellIsin}|${pfCode}`;
      const candidates = (dpDebitByCode.get(dpSellKey) ?? []).filter(dp => !usedDp.has(dp.id));
      const dpTotal    = candidates.reduce((s, dp) => s + Number(dp.dp_debit ?? 0), 0);
      if (candidates.length > 0 && Math.abs(dpTotal - totalQty) < 0.001) matchedDps = candidates;
    }

    if (matchedDps.length === 0) {
      // Fallback: date + qty (DP code may differ from PF code)
      for (const dp of dpPairs) {
        if (usedDp.has(dp.id)) continue;
        const debit = Number(dp.dp_debit ?? 0);
        if (debit <= 0 || dp.dp_txn_date !== sellDate) continue;
        if (Math.abs(debit - totalQty) < 0.001) { matchedDps = [dp]; break; }
      }
    }

    matchedDps.forEach(dp => usedDp.add(dp.id));
    const matched = matchedDps[0] ?? null;
    orderedBands.push({ pfRows: nonIntradaySells, dpRow: matched, colour: matched ? 'green' : 'red', isGroup: nonIntradaySells.length > 1, sortDate: sellDate });
  }

  const dpCredByCode = new Map<string, ReconTxnPair[]>();
  for (const dp of dpPairs) {
    const credit = Number(dp.dp_credit ?? 0);
    if (credit <= 0) continue;
    const dpCode = extractDpSettlementCode(dp.dp_description);
    if (!dpCode) continue;
    const dpKey = `${dp.canonical_isin}|${dpCode}`;
    if (!dpCredByCode.has(dpKey)) dpCredByCode.set(dpKey, []);
    dpCredByCode.get(dpKey)!.push(dp);
  }
  for (const [k, rows] of dpCredByCode.entries()) dpCredByCode.set(k, dedupeEdisLines(rows));

  // ── NET MATCHING PASS ─────────────────────────────────────────────────
  // Same code, same date: BUY total - SELL total = DP net delivery qty.
  for (const [code, buyGroup] of buyByCode.entries()) {
    const sellGroup = sellByCode.get(code);
    if (!sellGroup) continue;
    const buyByDate  = new Map<string, ReconTxnPair[]>();
    const sellByDate2 = new Map<string, ReconTxnPair[]>();
    for (const r of buyGroup.rows) {
      const d = r.pf_txn_date ?? '';
      if (!buyByDate.has(d)) buyByDate.set(d, []);
      buyByDate.get(d)!.push(r);
    }
    for (const r of sellGroup.rows) {
      const d = r.pf_txn_date ?? '';
      if (!sellByDate2.has(d)) sellByDate2.set(d, []);
      sellByDate2.get(d)!.push(r);
    }
    for (const [date, buyRows] of buyByDate.entries()) {
      const sellRows2 = sellByDate2.get(date);
      if (!sellRows2) continue;
      const buyTotal  = buyRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      const sellTotal = sellRows2.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      const net = buyTotal - sellTotal;
      if (Math.abs(net) < 0.001) continue;
      if (net > 0) {
        const netIsin    = buyRows[0]?.canonical_isin ?? '';
        const netKey     = `${netIsin}|${code}`;
        const candidates = (dpCredByCode.get(netKey) ?? []).filter(dp => !usedDp.has(dp.id));
        const dpTotal2 = candidates.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);
        if (candidates.length > 0 && Math.abs(dpTotal2 - net) < 0.001) {
          candidates.forEach(dp => usedDp.add(dp.id));
          orderedBands.push({ pfRows: buyRows, dpRow: candidates[0], colour: 'green', isGroup: buyRows.length > 1, sortDate: date });
          orderedBands.push({ pfRows: sellRows2, dpRow: null, colour: 'red', isGroup: sellRows2.length > 1, sortDate: date });
          buyRows.forEach(r => usedDp.add(r.id + '__pfUsed'));
          sellRows2.forEach(r => usedDp.add(r.id + '__pfUsed'));
        }
      } else {
        const absNet = Math.abs(net);
        const netIsin2   = sellRows2[0]?.canonical_isin ?? '';
        const netKey2    = `${netIsin2}|${code}`;
        const candidates = (dpDebitByCode.get(netKey2) ?? []).filter(dp => !usedDp.has(dp.id));
        const dpTotal2 = candidates.reduce((s, dp) => s + Number(dp.dp_debit ?? 0), 0);
        if (candidates.length > 0 && Math.abs(dpTotal2 - absNet) < 0.001) {
          candidates.forEach(dp => usedDp.add(dp.id));
          orderedBands.push({ pfRows: sellRows2, dpRow: candidates[0], colour: 'green', isGroup: sellRows2.length > 1, sortDate: date });
          orderedBands.push({ pfRows: buyRows, dpRow: null, colour: 'red', isGroup: buyRows.length > 1, sortDate: date });
          buyRows.forEach(r => usedDp.add(r.id + '__pfUsed'));
          sellRows2.forEach(r => usedDp.add(r.id + '__pfUsed'));
        }
      }
    }
  }

    for (const { code: pfCode, date, rows: allBuyRows } of buyByCode.values()) {
    const rows = allBuyRows.filter(r => !usedDp.has(r.id + '__pfUsed'));
    if (rows.length === 0) continue;
    const totalQty = rows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
    let matchedDps: ReconTxnPair[] = [];

    if (pfCode) {
      const buyIsin    = rows[0]?.canonical_isin ?? '';
      const dpBuyKey   = `${buyIsin}|${pfCode}`;
      const candidates = (dpCredByCode.get(dpBuyKey) ?? []).filter(dp => !usedDp.has(dp.id));
      const dpTotal    = candidates.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);
      if (candidates.length > 0 && Math.abs(dpTotal - totalQty) < 0.001) {
        matchedDps = candidates;
      } else {
        // Pass 4 — Tier-2 individual-first matching (bank-reference + blank-settlement):
        // "By HDFC BANK LIMITED/22288461", "By ICICI BANK LIMITED/90064449" etc.
        // have no exchange settlement code → isTier2CreditVfy() catches them.
        // Individual single-row match FIRST; subset match second; consume-once.
        const pfDateMs = date ? new Date(date).getTime() : 0;
        const remainingPf4 = [...rows];
        const pass4Pairs: Array<{ pfRows: ReconTxnPair[]; dp: ReconTxnPair }> = [];

        // Pool all Tier-2 DP credits in window, earliest first
        const tier2Pool4 = dpPairs.filter(dp => {
          if (usedDp.has(dp.id)) return false;
          const credit = Number(dp.dp_credit ?? 0);
          if (credit <= 0) return false;
          if (!isTier2CreditVfy(dp.dp_description)) return false;
          if (!dp.dp_txn_date || !pfDateMs) return true;
          const dm = new Date(dp.dp_txn_date).getTime();
          return dm >= pfDateMs && (dm - pfDateMs) <= 10 * 24 * 60 * 60 * 1000;
        }).sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? ''));

        for (const dp4 of tier2Pool4) {
          const dpQty4 = Number(dp4.dp_credit ?? 0);
          // Step 1: single PF row of equal qty
          const singles4 = remainingPf4.filter(r => Math.abs(Math.abs(Number(r.pf_qty ?? 0)) - dpQty4) < 0.001);
          singles4.sort((a, b) => Math.abs(new Date(a.pf_txn_date ?? '').getTime() - new Date(dp4.dp_txn_date ?? '').getTime()) - Math.abs(new Date(b.pf_txn_date ?? '').getTime() - new Date(dp4.dp_txn_date ?? '').getTime()));
          if (singles4.length > 0) {
            pass4Pairs.push({ pfRows: [singles4[0]], dp: dp4 });
            remainingPf4.splice(remainingPf4.indexOf(singles4[0]), 1);
            continue;
          }
          // Step 2: smallest subset summing to dpQty4
          const n4 = Math.min(remainingPf4.length, 16);
          let best4: ReconTxnPair[] | null = null;
          for (let mask = 1; mask < (1 << n4); mask++) {
            let sum4 = 0; const sub4: ReconTxnPair[] = [];
            for (let bit = 0; bit < n4; bit++) {
              if (mask & (1 << bit)) { sub4.push(remainingPf4[bit]); sum4 += Math.abs(Number(remainingPf4[bit].pf_qty ?? 0)); }
              if (sum4 > dpQty4 + 0.001) break;
            }
            if (Math.abs(sum4 - dpQty4) < 0.001 && (!best4 || sub4.length < best4.length)) best4 = sub4;
          }
          if (best4) {
            pass4Pairs.push({ pfRows: best4, dp: dp4 });
            best4.forEach(r => remainingPf4.splice(remainingPf4.indexOf(r), 1));
          }
        }

        // Commit matched pairs; unmatched PF rows stay in matchedDps=[] → RED band
        if (pass4Pairs.length > 0) {
          pass4Pairs.forEach(({ pfRows: p4pf, dp: p4dp }) => {
            usedDp.add(p4dp.id);
            orderedBands.push({ pfRows: p4pf, dpRow: p4dp, colour: 'green', isGroup: p4pf.length > 1, sortDate: date });
          });
          // Any remaining unmatched PF rows → red
          if (remainingPf4.length > 0) {
            orderedBands.push({ pfRows: remainingPf4, dpRow: null, colour: 'red', isGroup: remainingPf4.length > 1, sortDate: date });
          }
          matchedDps = []; // signal that we handled emission above
        }
      }
    } else {
      for (const dp of dpPairs) {
        if (usedDp.has(dp.id)) continue;
        const credit = Number(dp.dp_credit ?? 0);
        if (credit <= 0) continue;
        if (Math.abs(credit - totalQty) < 0.001) { matchedDps = [dp]; break; }
      }
    }

    // When Pass 4 already emitted bands directly (pass4Pairs.length > 0),
    // matchedDps was set to [] as a signal — skip the default emission.
    const pass4Handled = pfCode !== null && matchedDps.length === 0 &&
      rows.some(r => !usedDp.has(r.id)); // some rows already emitted above
    if (!pass4Handled) {
      matchedDps.forEach(dp => usedDp.add(dp.id));
      const matched = matchedDps[0] ?? null;
      orderedBands.push({ pfRows: rows, dpRow: matched, colour: matched ? 'green' : 'red', isGroup: rows.length > 1, sortDate: date });
    }
  }

  // ── Tier-3 pledge-recovery pass (mirrors TxnReconView) ────────────────────
  // PF rows flagged is_tier3_match by the backend recover against a pledge/
  // hidden DP entry. Group split rows by code+date, match combined qty to one
  // DP entry (latest within 10 days). Only applies to rows still in RED bands.
  const tier3DpPairs = dpPairs.filter(dp => dp.is_tier3_match && !usedDp.has(dp.id));
  if (tier3DpPairs.length > 0) {
    const tier3PfRows = allPairs.filter(p => p.pf_qty != null && p.is_tier3_match);
    const grp = new Map<string, ReconTxnPair[]>();
    for (const pr of tier3PfRows) {
      const code = (pr.pf_particulars ?? '').trim();
      const date = pr.pf_txn_date ?? '';
      const isBuy = Number(pr.pf_qty ?? 0) > 0;
      const key = `${code}|${date}|${isBuy ? 'B' : 'S'}`;
      if (!grp.has(key)) grp.set(key, []);
      grp.get(key)!.push(pr);
    }
    for (const rows of grp.values()) {
      const anyGreen = rows.some(pr => {
        const b = orderedBands.find(bd => bd.pfRows.some(r => r.id === pr.id));
        return b && b.colour !== 'red';
      });
      if (anyGreen) continue;
      const gQty = rows.reduce((s, r) => s + Math.abs(Number(r.pf_qty ?? 0)), 0);
      const pfMs = rows[0].pf_txn_date ? new Date(rows[0].pf_txn_date).getTime() : 0;
      const match = tier3DpPairs.find(dp => {
        if (usedDp.has(dp.id)) return false;
        const dq = Number(dp.dp_credit ?? 0) + Number(dp.dp_debit ?? 0);
        if (Math.abs(dq - gQty) >= 0.001) return false;
        if (!dp.dp_txn_date || !pfMs) return true;
        const dm = new Date(dp.dp_txn_date).getTime();
        return dm >= pfMs && (dm - pfMs) <= 10 * 24 * 60 * 60 * 1000;
      });
      if (!match) continue;
      usedDp.add(match.id);
      for (const pr of rows) {
        const idx = orderedBands.findIndex(b => b.pfRows.some(r => r.id === pr.id));
        if (idx >= 0) orderedBands.splice(idx, 1);
      }
      orderedBands.push({ pfRows: rows, dpRow: match, colour: 'green', isGroup: rows.length > 1, sortDate: rows[0].pf_txn_date! });
    }
  }

  // Unmatched DP rows become DP-ONLY bands so they slot into the SAME
  // chronological sequence as PF bands — never appended at the bottom
  // regardless of their actual date. Pledge entries are hidden unless linked.
  const unmatchedDp = dpPairs.filter(dp => !usedDp.has(dp.id) && !isPledgeDescriptionVfy(dp.dp_description));
  for (const dp of unmatchedDp) {
    orderedBands.push({ pfRows: [], dpRow: dp, colour: 'red', isGroup: false, sortDate: dp.dp_txn_date! });
  }

  // Sort the ENTIRE unified list chronologically by sortDate.
  orderedBands.sort((a, b) => (a.sortDate > b.sortDate ? 1 : a.sortDate < b.sortDate ? -1 : 0));

  return { bands: orderedBands };
}

/**
 * MatchedTxnTable — unified side-by-side view with green/red band colouring.
 * Replaces the old independent ParsedPfTable + ParsedDpTable pair.
 *
 * Rules:
 *   BUY groups    → matched to DP CREDIT within 1–3 days, same total qty → GREEN
 *   SELL groups   → matched to DP DEBIT on exact same date, same qty → GREEN
 *   DIVIDENDS     → auto-GREEN, no DP counterpart needed
 *   Unmatched PF  → RED
 *   Unmatched DP  → RED, appended at bottom
 */
function ParsedPfTable({
  isin,
  rows,
  pairsByRow,
  unpairedIds: _unpairedIds,  // kept for API compat but replaced by matching engine
}: {
  isin: string;
  rows: ReconRow[];
  pairsByRow: Map<string, ReconTxnPair[]>;
  unpairedIds?: Set<string>;
}) {
  const row = rows.find(r => r.canonical_isin === isin);
  const allPairs = row ? (pairsByRow.get(row.id) ?? []) : [];
  return <MatchedTxnTable allPairs={allPairs} isin={isin} />;
}

function ParsedDpTable({
  isin,
  rows,
  pairsByRow,
  unpairedIds: _unpairedIds,  // kept for API compat but replaced by matching engine
}: {
  isin: string;
  filename: string;
  rows: ReconRow[];
  pairsByRow: Map<string, ReconTxnPair[]>;
  unpairedIds?: Set<string>;
}) {
  const row = rows.find(r => r.canonical_isin === isin);
  const allPairs = row ? (pairsByRow.get(row.id) ?? []) : [];
  return <MatchedTxnTable allPairs={allPairs} isin={isin} />;
}

function MatchedTxnTable({ allPairs, isin }: { allPairs: ReconTxnPair[]; isin: string }) {
  const { bands } = useMemo(() => buildVfyBands(allPairs), [allPairs]);

  const pfCount  = allPairs.filter(p => p.pf_txn_date != null).length;
  const dpCount  = allPairs.filter(p => p.pf_txn_date == null && p.dp_txn_date != null).length;
  const unmatchedCount = bands.filter(b => b.colour === 'red').length;

  const fmtQty = (n: number | null | undefined) => {
    if (n == null || n === 0) return '—';
    return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });
  };
  const fmtPrice = (n: number | null | undefined) => {
    if (n == null) return '—';
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtDate = (d: string | null | undefined) => (!d ? '—' : d.slice(0, 10));

  if (pfCount === 0 && dpCount === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm py-12"
           style={{ color: 'var(--text-200)' }}>
        No transactions for {isin}.
      </div>
    );
  }

  // Column widths shared across both sides so they stay perfectly in line.
  // Using a single 8-col grid per row: [PF date | PF type | PF qty | PF price | divider | DP date | DP desc | DP cr/dr]
  // col widths: 1fr 2fr 1fr 1.5fr | 3px | 1fr 2fr 1fr
  const ROW_COLS = 'grid-cols-[1fr_2fr_1fr_1.5fr_3px_1fr_2fr_1fr]';
  const HDR_COLS = 'grid-cols-[1fr_2fr_1fr_1.5fr_3px_1fr_2fr_1fr]';

  return (
    <div className="flex-1 overflow-auto p-3" style={{ background: 'var(--bg-surface)' }}>
      <div className="text-xs mb-3 flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-200)' }}>
        <span>
          <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{pfCount} portfolio</span>
          {' '}· <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{dpCount} DP</span>
          {' '}transactions for {isin}
        </span>
        {unmatchedCount > 0 && (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                style={{ background: 'color-mix(in srgb, var(--status-err) 15%, transparent)', color: 'var(--status-err)' }}>
            {unmatchedCount} unmatched
          </span>
        )}
      </div>

      <div className="rounded-md overflow-hidden border" style={{ borderColor: 'var(--glass-border)' }}>

        {/* Section header row */}
        <div className={`grid ${HDR_COLS}`} style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--glass-border)' }}>
          <div className="col-span-4 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider"
               style={{ color: 'var(--status-info)', borderRight: '1px solid var(--glass-border)' }}>
            Portfolio · {pfCount} txn
          </div>
          <div style={{ background: 'var(--glass-border)' }} />
          <div className="col-span-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider"
               style={{ color: 'var(--status-ok)' }}>
            Depository (DP) · {dpCount} txn
          </div>
        </div>

        {/* Column sub-headers */}
        <div className={`grid ${HDR_COLS} text-[10px] uppercase tracking-wider font-semibold`}
             style={{ background: 'var(--bg-elevated)', color: 'var(--text-400)', borderBottom: '1px solid var(--glass-border)' }}>
          <div className="px-3 py-1.5" style={{ borderRight: '1px solid var(--glass-border)' }}>Date</div>
          <div className="px-1 py-1.5" style={{ borderRight: '1px solid var(--glass-border)' }}>Type</div>
          <div className="px-1 py-1.5 text-right" style={{ borderRight: '1px solid var(--glass-border)' }}>Qty</div>
          <div className="px-1 py-1.5 text-right" style={{ borderRight: '1px solid var(--glass-border)' }}>Price / Value</div>
          <div style={{ background: 'var(--glass-border)' }} />
          <div className="px-3 py-1.5" style={{ borderRight: '1px solid var(--glass-border)' }}>Date</div>
          <div className="px-1 py-1.5" style={{ borderRight: '1px solid var(--glass-border)' }}>Description</div>
          <div className="px-1 py-1.5 text-right">Cr / Dr</div>
        </div>

        {/* Single chronologically-sorted list — matched bands AND
            unmatched-DP-only bands interleaved by date. */}
        {bands.map((band, i) => {
          const isGreen = band.colour === 'green';
          const isDividend = isGreen && band.dpRow == null;
          const pfBg = isGreen
            ? 'color-mix(in srgb, var(--status-ok) 12%, transparent)'
            : 'color-mix(in srgb, var(--status-err) 12%, transparent)';
          const pfBorderL = isGreen ? '3px solid var(--status-ok)' : '3px solid var(--status-err)';
          const dpBg = isGreen ? 'color-mix(in srgb, var(--status-ok) 12%, transparent)' : 'transparent';

          // DP-only band: an unmatched DP row with no PF counterpart.
          // Render a single row with the PF side empty.
          if (band.pfRows.length === 0 && band.dpRow) {
            const dp = band.dpRow;
            return (
              <div key={`dp-${dp.id}`} className={`grid ${ROW_COLS} text-xs items-center`}
                   style={{ borderTop: '1px solid var(--glass-border)' }}>
                {/* PF side — empty */}
                <div className="px-3 py-2" style={{ borderRight: '1px solid var(--glass-border)', color: 'var(--text-400)' }}>—</div>
                <div style={{ borderRight: '1px solid var(--glass-border)' }} />
                <div style={{ borderRight: '1px solid var(--glass-border)' }} />
                <div style={{ borderRight: '1px solid var(--glass-border)' }} />
                {/* Divider */}
                <div style={{ background: 'var(--glass-border)', alignSelf: 'stretch' }} />
                {/* DP date */}
                <div className="px-3 py-2 font-mono text-[11px]"
                     style={{ background: 'color-mix(in srgb, var(--status-err) 12%, transparent)', borderLeft: '3px solid var(--status-err)', color: 'var(--text-strong)', borderRight: '1px solid var(--glass-border)' }}>
                  {fmtDate(dp.dp_txn_date)}
                </div>
                {/* DP description */}
                <div className="px-1 py-2 text-[11px] truncate cursor-pointer select-text"
                     style={{ background: 'color-mix(in srgb, var(--status-err) 12%, transparent)', color: 'var(--text-strong)', borderRight: '1px solid var(--glass-border)' }}
                     title={dp.dp_description ?? ''}
                     onClick={e => {
                       const el = e.currentTarget;
                       if (el.style.whiteSpace === 'normal') { el.style.whiteSpace = ''; el.style.overflow = ''; el.classList.add('truncate'); }
                       else { el.style.whiteSpace = 'normal'; el.style.overflow = 'visible'; el.classList.remove('truncate'); }
                     }}>
                  {dp.dp_description ?? '—'}
                  <span className="ml-1 text-[10px] font-bold" style={{ color: 'var(--status-err)' }}>✗</span>
                </div>
                {/* DP cr/dr */}
                <div className="px-1 py-2 text-right font-mono text-[11px]"
                     style={{ background: 'color-mix(in srgb, var(--status-err) 12%, transparent)', color: 'var(--text-strong)' }}>
                  {dp.dp_credit != null && dp.dp_credit !== 0
                    ? <span style={{ color: 'var(--status-ok)' }}>+{fmtQty(dp.dp_credit)}</span>
                    : dp.dp_debit != null && dp.dp_debit !== 0
                      ? <span style={{ color: 'var(--status-err)' }}>−{fmtQty(dp.dp_debit)}</span>
                      : '—'}
                </div>
              </div>
            );
          }

          return (
            <div key={i} style={{ borderTop: '1px solid var(--glass-border)' }}>
              {band.pfRows.map((p, idx) => {
                const isLast = idx === band.pfRows.length - 1;
                // DP content only on the last PF row of the group (vertically centred)
                const showDp = isLast;
                return (
                  <div key={p.id} className={`grid ${ROW_COLS} text-xs items-center`}
                       style={{ borderTop: idx > 0 ? '1px solid var(--glass-border)' : undefined }}>
                    {/* PF date */}
                    <div className="px-3 py-2 font-mono text-[11px]"
                         style={{ background: pfBg, borderLeft: pfBorderL, color: 'var(--text-strong)', borderRight: '1px solid var(--glass-border)' }}>
                      {fmtDate(p.pf_txn_date)}
                    </div>
                    {/* PF type */}
                    <div className="px-1 py-2"
                         style={{ background: pfBg, color: 'var(--text-strong)', borderRight: '1px solid var(--glass-border)' }}>
                      {p.pf_txn_type}
                      {p.pf_particulars && (
                        <span
                          className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded"
                          style={{ background: 'rgba(0,0,0,0.06)', color: 'var(--text-400)', border: '1px solid var(--glass-border)' }}
                          title="Settlement code (Particulars)"
                        >
                          {p.pf_particulars}
                        </span>
                      )}
                      {band.isGroup && isLast && (
                        <span className="ml-1 text-[10px] px-1 rounded"
                              style={{ background: isGreen ? 'color-mix(in srgb,var(--status-ok) 25%,transparent)' : 'color-mix(in srgb,var(--status-err) 25%,transparent)', color: isGreen ? 'var(--status-ok)' : 'var(--status-err)' }}>
                          Σ{band.pfRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0)}
                        </span>
                      )}
                      {!isGreen && <span className="ml-1 text-[10px] font-bold" style={{ color: 'var(--status-err)' }}>✗</span>}
                      {isDividend && <span className="ml-1 text-[10px] font-bold" style={{ color: 'var(--status-ok)' }}>✓ auto</span>}
                      {isGreen && !isDividend && isLast && <span className="ml-1 text-[10px] font-bold" style={{ color: 'var(--status-ok)' }}>✓</span>}
                    </div>
                    {/* PF qty */}
                    <div className="px-1 py-2 text-right font-mono"
                         style={{ background: pfBg, color: 'var(--text-strong)', borderRight: '1px solid var(--glass-border)' }}>
                      {fmtQty(p.pf_qty)}
                    </div>
                    {/* PF price */}
                    <div className="px-1 py-2 text-right font-mono text-[11px]"
                         style={{ background: pfBg, color: 'var(--text-strong)', borderRight: '1px solid var(--glass-border)' }}>
                      {fmtPrice(p.pf_trade_price)}
                      {p.pf_trade_value != null && (
                        <span className="block text-[10px]" style={{ color: 'var(--text-400)' }}>tot {fmtPrice(p.pf_trade_value)}</span>
                      )}
                    </div>
                    {/* Divider */}
                    <div style={{ background: 'var(--glass-border)', alignSelf: 'stretch' }} />
                    {/* DP date */}
                    <div className="px-3 py-2 font-mono text-[11px]"
                         style={{ background: showDp ? dpBg : undefined, color: 'var(--text-strong)', borderRight: '1px solid var(--glass-border)', minHeight: '36px' }}>
                      {showDp && band.dpRow ? fmtDate(band.dpRow.dp_txn_date) : showDp && isDividend ? '' : ''}
                    </div>
                    {/* DP description */}
                    <div className="px-1 py-2 text-[11px] truncate cursor-pointer select-text"
                         style={{ background: showDp ? dpBg : undefined, color: 'var(--text-strong)', borderRight: '1px solid var(--glass-border)' }}
                         title={showDp && band.dpRow ? (band.dpRow.dp_description ?? '') : ''}
                         onClick={e => {
                           const el = e.currentTarget;
                           if (el.style.whiteSpace === 'normal') { el.style.whiteSpace = ''; el.style.overflow = ''; el.classList.add('truncate'); }
                           else { el.style.whiteSpace = 'normal'; el.style.overflow = 'visible'; el.classList.remove('truncate'); }
                         }}>
                      {showDp && band.dpRow
                        ? band.dpRow.dp_description ?? '—'
                        : showDp && isDividend
                          ? <span style={{ color: 'var(--status-ok)', fontStyle: 'italic' }}>✓ dividend — no DP entry</span>
                          : ''}
                    </div>
                    {/* DP cr/dr */}
                    <div className="px-1 py-2 text-right font-mono text-[11px]"
                         style={{ background: showDp ? dpBg : undefined, color: 'var(--text-strong)' }}>
                      {showDp && band.dpRow && band.dpRow.dp_credit != null && band.dpRow.dp_credit !== 0
                        ? <span style={{ color: 'var(--status-ok)' }}>+{fmtQty(band.dpRow.dp_credit)}</span>
                        : showDp && band.dpRow && band.dpRow.dp_debit != null && band.dpRow.dp_debit !== 0
                          ? <span style={{ color: 'var(--status-err)' }}>−{fmtQty(band.dpRow.dp_debit)}</span>
                          : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// useMemo import used inside MatchedTxnTable — pulled in from React at top of file.


